import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { interval, map, startWith, Subscription, switchMap } from 'rxjs';
import { API_ENDPOINTS } from '../api/endpoints';
import { mapRecomputeJob, RecomputeJob } from '../models/recompute.model';
import { AuthService } from './auth.service';
import { CongressSelectorService } from './congress-selector.service';
import { PendingToastService } from './pending-toast.service';

interface PersistedJob {
  readonly slug: string;
  readonly annee: string;
  readonly jobId: number;
}

const STORAGE_KEY = 'recompute-job';
const POLL_MS = 1500;
const MAX_POLL_MS = 120_000; // garde-fou : on cesse de poller au bout de 2 min

/**
 * État global du recalcul asynchrone des données (déclenché par la sauvegarde des paramètres).
 * Singleton root : l'affichage est un toast global (gabarit `#headless` dans app.html), visible
 * quelle que soit la page.
 *
 * Admin uniquement (les endpoints sont sous `^/api/admin`). Survit à la navigation et reprend
 * après un rechargement de page (sessionStorage + lookup `recompute/current`).
 */
@Injectable({ providedIn: 'root' })
export class RecomputeStatusService {
  private readonly http = inject(HttpClient);
  private readonly congress = inject(CongressSelectorService);
  private readonly auth = inject(AuthService);
  private readonly pendingToast = inject(PendingToastService);
  private readonly toast = inject(MessageService);

  private readonly _job = signal<RecomputeJob | null>(null);
  readonly job = this._job.asReadonly();

  private sub?: Subscription;
  /** Recherche d'un recalcul lancé ailleurs (GET recompute/current), annulée avec le sondage. */
  private lookup?: Subscription;
  private deadline = 0;
  /** Évite de ré-émettre (et donc de faire clignoter) le toast « en cours » à chaque sondage. */
  private enCoursAffiche = false;

  constructor() {
    // Reprise : à chaque sélection de congrès (admin), on récupère un éventuel recalcul en cours.
    effect(() => {
      const c = this.congress.selected();
      if (c && this.auth.isAdmin()) {
        this.resumeFor(c.slug, c.annee);
      } else {
        this.stopPolling();
        this.effaceToast();
        this._job.set(null);
      }
    });
  }

  /**
   * Affiche immédiatement le toast « en cours », avant même d'avoir l'id du job (réponse du PUT).
   * Donne un retour visuel instantané au clic ; le polling prend ensuite le relais via track().
   */
  begin(): void {
    this.notifieEnCours();
    this._job.set({
      id: 0,
      status: 'running',
      snapshotsCount: null,
      startedAt: null,
      finishedAt: null,
      error: null,
    });
  }

  /** Annule l'affichage optimiste (sauvegarde échouée, ou aucun job à suivre). */
  cancel(): void {
    this.stopPolling();
    this.clearPersist();
    this.effaceToast();
    this._job.set(null);
  }

  /** Démarre le suivi d'un job (appelé après la sauvegarde des paramètres). */
  track(slug: string, annee: string, jobId: number): void {
    this.persist({ slug, annee, jobId });
    this.startPolling(slug, annee, jobId);
  }

  private resumeFor(slug: string, annee: string): void {
    // Changement d'édition : un toast qui parle du recalcul de la précédente n'a plus de sens.
    this.stopPolling();
    this.effaceToast();
    this._job.set(null);

    const stored = this.readPersist();
    if (stored && stored.slug === slug && stored.annee === annee) {
      this.startPolling(slug, annee, stored.jobId);
      return;
    }
    // Pas de job local : un autre onglet/admin a peut-être lancé un recalcul → on demande au back.
    this.lookup = this.http
      .get<unknown>(API_ENDPOINTS.adminCongres.recomputeCurrent(slug, annee))
      .pipe(map(mapRecomputeJob))
      .subscribe({
        next: (job) => {
          if (job && (job.status === 'pending' || job.status === 'running')) {
            this.track(slug, annee, job.id);
          }
        },
        error: () => {
          /* 403 (non-admin) / réseau : on ignore, pas d'indicateur */
        },
      });
  }

  private startPolling(slug: string, annee: string, jobId: number): void {
    this.stopPolling();
    this.deadline = Date.now() + MAX_POLL_MS;
    this.sub = interval(POLL_MS)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.http.get<unknown>(API_ENDPOINTS.adminCongres.recomputeStatus(slug, annee, jobId)),
        ),
        map(mapRecomputeJob),
      )
      .subscribe({
        next: (job) => {
          if (!job) {
            return;
          }
          this._job.set(job);
          if (job.status === 'pending' || job.status === 'running') {
            // Reprise (rechargement de page, recalcul lancé depuis un autre onglet) : le toast
            // n'a pas été émis par begin(), on l'affiche ici. C'est le seul affichage du recalcul
            // depuis le retrait de la pastille de l'appbar.
            this.notifieEnCours();
          }
          if (job.status === 'done') {
            this.onDone();
          } else if (job.status === 'failed') {
            this.onFailed(job);
          } else if (Date.now() > this.deadline) {
            this.stopPolling();
            this.clearPersist();
          }
        },
        error: () => {
          this.stopPolling();
          this.clearPersist();
        },
      });
  }

  private onDone(): void {
    this.enCoursAffiche = false;
    this.stopPolling();
    this.clearPersist();
    // Recalcul terminé : on NE recharge PAS automatiquement. Le toast bascule sur un bouton
    // « Rafraîchir la page » ; l'utilisateur recharge quand il le souhaite via reloadNow().
    this.toast.clear('recompute');
    this.toast.add({
      key: 'recompute',
      severity: 'success',
      summary: 'Recalcul terminé',
      detail: 'Les données recalculées sont prêtes.',
      sticky: true,
      data: { state: 'done' },
    });
  }

  /**
   * Notification « travail en cours », persistante (`sticky`) : la durée dépend du volume de
   * l'édition, donc on ne la fait pas disparaître toute seule — elle est remplacée par celle de
   * fin, ou retirée par `cancel()`. La barre est indéterminée : le back ne remonte pas de
   * pourcentage, mieux vaut montrer une activité qu'une fausse progression.
   */
  private notifieEnCours(): void {
    if (this.enCoursAffiche) {
      return;
    }
    this.enCoursAffiche = true;
    this.toast.clear('recompute');
    this.toast.add({
      key: 'recompute',
      severity: 'info',
      summary: 'Recalcul des données en cours',
      detail: 'Les indicateurs se mettent à jour, tu peux continuer à naviguer.',
      sticky: true,
      data: { state: 'running' },
    });
  }

  /**
   * Rechargement complet de la page, déclenché par l'utilisateur depuis l'indicateur « terminé ».
   * Le toast de confirmation est mémorisé pour être émis APRÈS le reload (PendingToastService,
   * flush par App au démarrage).
   */
  reloadNow(): void {
    this.pendingToast.queueForReload({
      severity: 'success',
      summary: 'Données mises à jour',
      detail: 'La page a été rafraîchie avec les nouvelles données.',
      life: 3500,
    });
    window.location.reload();
  }

  private onFailed(job: RecomputeJob): void {
    this.enCoursAffiche = false;
    this.stopPolling();
    this.clearPersist();
    this.toast.clear('recompute');
    this.toast.add({
      severity: 'error',
      summary: 'Recalcul échoué',
      detail: job.error ?? 'Une erreur est survenue pendant le recalcul.',
      life: 6000,
    });
    this._job.set(null);
  }

  /** Retire le toast du recalcul et réarme son émission. */
  private effaceToast(): void {
    this.enCoursAffiche = false;
    this.toast.clear('recompute');
  }

  /** Coupe le sondage ET une recherche de recalcul en vol : sa réponse tardive lancerait le suivi
   *  de l'édition quittée. */
  private stopPolling(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    this.lookup?.unsubscribe();
    this.lookup = undefined;
  }

  private persist(job: PersistedJob): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(job));
    } catch {
      /* sessionStorage indisponible : le suivi reste en mémoire pour cette session */
    }
  }

  private readPersist(): PersistedJob | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      const o =
        typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
      const slug = typeof o['slug'] === 'string' ? o['slug'] : null;
      const annee = typeof o['annee'] === 'string' ? o['annee'] : null;
      const jobId = typeof o['jobId'] === 'number' ? o['jobId'] : null;
      return slug && annee && jobId !== null ? { slug, annee, jobId } : null;
    } catch {
      return null;
    }
  }

  private clearPersist(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* no-op */
    }
  }
}
